import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { TabIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import {
  financeLegacyService,
  FinanceLegacyEntry,
  FinanceLegacyOptions,
  ENTRY_TYPE_INCOME,
} from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// entries.type: 1 = розхід, 2 = дохід (немає довідникової таблиці, конвенція
// підтверджена користувачем на живому перегляді).
const EntryTypeChip: React.FC<{ type: number }> = ({ type }) => (
  type === ENTRY_TYPE_INCOME
    ? <Chip label="Дохід" color="success" size="small" variant="outlined" />
    : <Chip label="Розхід" color="error" size="small" variant="outlined" />
);

export const FinanceLegacyEntriesTab: React.FC<{ options: FinanceLegacyOptions }> = ({ options }) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [entries, setEntries] = useState<FinanceLegacyEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const result = await financeLegacyService.getEntries({
        from: fromDate ? fromDate.toISOString() : undefined,
        to: toDate ? toDate.toISOString() : undefined,
        projectId: projectFilter ? Number(projectFilter) : undefined,
        sourceId: sourceFilter ? Number(sourceFilter) : undefined,
        page,
        limit,
      });
      setEntries(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити записи.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, projectFilter, sourceFilter, page, limit]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const projectLabel = (id: number | null) => options.projects.find((p) => p.value === id)?.label ?? id ?? '—';
  const sourceLabel = (id: number | null) => options.sources.find((s) => s.value === id)?.label ?? id ?? '—';
  const currencyLabel = (id: number | null) => options.currencies.find((c) => c.value === id)?.label ?? id ?? '—';

  return (
    <Box data-i-doc={TabIndices.DEV_FINANCE_LEGACY_ENTRIES}>
      <PageHint iDoc={TabIndices.DEV_FINANCE_LEGACY_ENTRIES} />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
        <PageToolbar
          storageKey="filters:dev-finance-legacy-entries"
          filters={[
            {
              id: 'project',
              type: 'select',
              label: 'Проєкт',
              value: projectFilter,
              onChange: (v) => { setProjectFilter(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, ...options.projects.map((p) => ({ value: String(p.value), label: p.label }))],
            },
            {
              id: 'source',
              type: 'select',
              label: 'Джерело',
              value: sourceFilter,
              onChange: (v) => { setSourceFilter(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, ...options.sources.map((s) => ({ value: String(s.value), label: s.label }))],
            },
            {
              id: 'from',
              type: 'custom',
              value: fromDate,
              renderCustom: () => (
                <DatePicker
                  label="Від"
                  value={fromDate}
                  onChange={(v) => { setFromDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
            {
              id: 'to',
              type: 'custom',
              value: toDate,
              renderCustom: () => (
                <DatePicker
                  label="До"
                  value={toDate}
                  onChange={(v) => { setToDate(v); setPage(1); }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              ),
            },
          ]}
          onReset={() => { setFromDate(null); setToDate(null); setProjectFilter(''); setSourceFilter(''); setPage(1); }}
          showReset
        />
      </LocalizationProvider>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Записів не знайдено.</Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Проєкт</TableCell>
                <TableCell>Джерело</TableCell>
                <TableCell>Сума</TableCell>
                <TableCell>Сума (грн)</TableCell>
                <TableCell>Коментар</TableCell>
                <TableCell>Співробітник (id)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{new Date(entry.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                  <TableCell><EntryTypeChip type={entry.type} /></TableCell>
                  <TableCell>{projectLabel(entry.projectId)}</TableCell>
                  <TableCell>{sourceLabel(entry.sourceId)}</TableCell>
                  <TableCell>{entry.amount ?? '—'} {currencyLabel(entry.currencyId)}</TableCell>
                  <TableCell>{entry.amountUah ?? '—'}</TableCell>
                  <TableCell>{entry.comment ?? '—'}</TableCell>
                  <TableCell>{entry.empId ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey="pagination:dev-finance-legacy-entries"
      />
    </Box>
  );
};
